import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import MarketingNav from "@/components/layout/MarketingNav";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !email.includes('@')) {
      toast.error("Please enter a valid email address!");
      return;
    }

    setLoading(true);
    try {
      // Use branded Resend Edge Function (not Auth SMTP default template).
      const { data, error } = await supabase.functions.invoke(
        "send-password-reset-email",
        {
          body: {
            email: email.trim(),
            redirect_to: `${window.location.origin}/auth/recovery`,
          },
        },
      );

      if (error) {
        console.error("Password reset error:", error);
        toast.error(error.message || "Failed to send reset email. Please try again.");
        return;
      }

      if (data && typeof data === "object" && "error" in data && data.error) {
        toast.error(String(data.error));
        return;
      }

      setSuccess(true);
      toast.success("Password reset email sent successfully!");
    } catch (error: any) {
      console.error("Password reset error:", error);
      toast.error(error.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-950">
      <MarketingNav />

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 lg:p-16 bg-[#f8faf9] dark:bg-gray-950">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {!success ? (
            <>
              <div className="mb-8">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                  Reset your password
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/80 dark:border-gray-800 shadow-xl shadow-gray-200/50 dark:shadow-none p-6 sm:p-8">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Work email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 dark:text-white focus:outline-none focus:ring-2 focus:ring-skutopia-500/30 focus:border-skutopia-500 text-sm"
                        placeholder="you@institution.com"
                        required
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-skutopia-600 hover:bg-skutopia-700 text-white py-3 rounded-xl font-medium"
                  >
                    {loading ? "Sending reset link..." : "Send reset link"}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    to="/partners/login"
                    className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to sign in
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="mb-8 text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                  Check your email
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  We've sent a password reset link to your email address. Please check your inbox and follow the instructions.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/80 dark:border-gray-800 shadow-xl shadow-gray-200/50 dark:shadow-none p-6 sm:p-8">
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <p className="font-medium mb-2">Didn't receive the email?</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Check your spam folder</li>
                      <li>• Make sure you entered the correct email address</li>
                      <li>• Wait a few minutes before trying again</li>
                    </ul>
                  </div>

                  <Button
                    onClick={() => {
                      setSuccess(false);
                      setEmail("");
                    }}
                    variant="outline"
                    className="w-full rounded-xl"
                  >
                    Try again
                  </Button>

                  <div className="text-center">
                    <Link
                      to="/partners/login"
                      className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to sign in
                    </Link>
                  </div>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPassword;
